import { useState, useMemo } from 'react';
import { ManterComNotaModal } from '@/components/kanban/ManterComNotaModal';
import { AcoesTriagem, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CheckCircle2, Loader2, Check, Ban, Clock, CalendarClock, History } from 'lucide-react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFinalizarPedidoAtivo } from './useFinalizarPedidoAtivo';
import { NumeroLinhaHaDialog } from './NumeroLinhaHaDialog';
import { useToast } from '@/hooks/use-toast';
import { CancelarItemModal } from '@/components/pedido/CancelarItemModal';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { TriagemStandardHeaders, TriagemStandardCells, TriagemStandardColGroup, STANDARD_SELECT_FIELDS, buildLinhasFormatadas, getOperadoraWithFallback, type TriagemStandardItem } from './TriagemStandardColumns';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { equipesDoBko } from '@/lib/fila-permissions';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { depoisDoClique, manterNaTriagem, marcarTelasDeForaDesatualizadas, meuUsuarioId, useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import {
  TRIAGEM_TH, TRIAGEM_TD, TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO,
  TRIAGEM_SELO_BASE, TRIAGEM_SELO_OK, TRIAGEM_SELO_PENDENTE, TRIAGEM_LARGURA_MINIMA_PADRAO,
} from '@/components/kanban/triagem/triagemTabela';
import { usePortabilidadeExtras, diasDesde } from '@/hooks/usePortabilidade';
import { cn } from '@/lib/utils';
import { HistoricoPortabilidadeDialog, SeloOperadora, SeloPreAtivo } from './PortabilidadeDialogs';
import { equipesDoLider, filtrarPeloLider } from '../triagem/triagemPedidoUtils';

interface TriagemPortabilidadeConcluidaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ItemPortabilidadeConcluida extends TriagemStandardItem {
  telefone: string;
  email: string;
  status_id: string;
  coluna_atual: string;
  quadro_atual: string;
}

type LinhaPlanilha = Parameters<typeof buildLinhasFormatadas>[0];

const TZ = 'America/Sao_Paulo';
const diaBr = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: TZ });

/** A agenda em relação a hoje: é o que decide se já pode concluir. */
function situacaoAgenda(iso: string) {
  if (!iso) return { rotulo: 'sem agenda', vencida: false, futura: false, texto: '—' };
  const d = new Date(iso);
  const hoje = diaBr(new Date());
  const dia = diaBr(d);
  const amanha = diaBr(new Date(Date.now() + 86_400_000));
  const texto = d.toLocaleString('pt-BR', { timeZone: TZ, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  if (dia < hoje) return { rotulo: 'vencida', vencida: true, futura: false, texto };
  if (dia === hoje) return { rotulo: d.getTime() <= Date.now() ? 'hoje · passou da hora' : 'hoje', vencida: d.getTime() <= Date.now(), futura: false, texto };
  return { rotulo: dia === amanha ? 'amanhã' : 'próximos dias', vencida: false, futura: true, texto };
}

export function TriagemPortabilidadeConcluidaModal({ isOpen, onClose }: TriagemPortabilidadeConcluidaModalProps) {
  const [cancelarItem, setCancelarItem] = useState<ItemPortabilidadeConcluida | null>(null);
  const [cancelarOpen, setCancelarOpen] = useState(false);
  const [manterItem, setManterItem] = useState<ItemPortabilidadeConcluida | null>(null);
  const [reagendarItem, setReagendarItem] = useState<string | null>(null);
  const [novaData, setNovaData] = useState('');
  const [motivoReagendar, setMotivoReagendar] = useState('');
  const [historico, setHistorico] = useState<{ itemId: string; razaoSocial: string; numeroPedido: string } | null>(null);
  const { finalizarPedidoAsync, numeroHaDialog } = useFinalizarPedidoAtivo();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useUserProfileContext();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  /* Só a cotação clicada fica ocupada; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). O (PN) Concluído é da linha. */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const chaveDaCotacao = (i: ItemPortabilidadeConcluida) => i.numero_cotacao_operadora || i.item_id;
  const rodar = (chave: string, fn: () => Promise<unknown>) => { void ocupar(chave, fn).catch(() => undefined); };

  // Manter — a cotação inteira desce numa ida (`fn_triagem_manter`). A
  // justificativa já foi gravada pelo diálogo; só desce o que ainda está aqui.
  const moverParaFinal = useMutation({
    mutationFn: async (itemId: string) => {
      const itemClicado = itens.find((i) => i.item_id === itemId);
      const cotacao = itemClicado?.numero_cotacao_operadora;
      const itensParaManter = cotacao
        ? itens.filter((i) => i.numero_cotacao_operadora === cotacao)
        : itemClicado ? [itemClicado] : [];
      if (!itemClicado || itensParaManter.length === 0) throw new Error('Item não encontrado');
      const ids = itensParaManter.map((i) => i.item_id);
      const r = await manterNaTriagem({ pedidoId: itemClicado.pedido_id, itens: ids, coluna: 'PORTABILIDADE AGENDADA' });
      return { count: r.mantidos, pedidoId: itemClicado.pedido_id, ids };
    },
    onSuccess: ({ count, pedidoId, ids }) => {
      toast({ title: 'Mantido', description: count > 1 ? `${count} itens da mesma cotação foram para o fim da fila.` : 'Item foi para o fim da fila.' });
      void depoisDoClique(queryClient, ['triagem-portabilidade-concluida'], { pedidoId, itens: ids }, 'desce');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  // Reagendar: a portabilidade não aconteceu na hora marcada (ou a data mudou).
  // ⛔ (Douglas 22/10/2026) A cotação inteira muda junto, a data é futura e o
  // motivo é obrigatório: só a linha clicada mudava, data passada destravava o
  // Concluído e o aviso ao cliente saía dividido.
  const reagendar = useMutation({
    mutationFn: async ({ item, data, motivo }: { item: ItemPortabilidadeConcluida; data: string; motivo: string }) => {
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(data)) throw new Error('Informe a data E a hora da nova portabilidade.');
      if (new Date(data + ':00-03:00').getTime() <= Date.now()) throw new Error('A nova data tem que ser depois de agora.');
      if (motivo.trim().length < 10) throw new Error('Escreva o motivo do reagendamento (10 letras ou mais).');
      const daCotacao = itens.filter((i) => i.pedido_id === item.pedido_id
        && (i.numero_cotacao_operadora || '') === (item.numero_cotacao_operadora || ''));
      const linhas = daCotacao.length ? daCotacao : [item];
      const usuarioId = await meuUsuarioId();
      if (!usuarioId) throw new Error('Sua sessão expirou. Entre novamente.');
      const anterior = item.data_agendamento ? situacaoAgenda(item.data_agendamento).texto : 'sem agenda';
      const nova = situacaoAgenda(data + ':00-03:00').texto;
      for (const linha of linhas) {
        const { error } = await supabase
          .from('itens_pedido')
          .update({ data_agendamento: data + '-03:00', agenda_tipo: 'PORTABILIDADE' })
          .eq('id', linha.item_id);
        if (error) throw error;
        const { error: erroHistorico } = await supabase.from('historico_status').insert({
          pedido_id: linha.pedido_id, item_id: linha.item_id,
          quadro_anterior: 'PRE_ATIVO', quadro_atual: 'PRE_ATIVO',
          coluna_anterior: linha.coluna_atual, coluna_atual: linha.coluna_atual,
          status_anterior_id: linha.status_id, status_atual_id: linha.status_id,
          user_id: usuarioId,
          motivo: `Portabilidade reagendada: ${anterior} → ${nova} · ${motivo.trim()}`,
        } as never);
        if (erroHistorico) throw erroHistorico;
      }
      return linhas.length;
    },
    onSuccess: (n) => {
      toast({ title: 'Portabilidade reagendada', description: `${n > 1 ? `${n} linhas da cotação` : 'A linha'} com a nova data. O cliente é avisado na véspera ou no dia do novo horário.` });
      setReagendarItem(null);
      setNovaData('');
      setMotivoReagendar('');
      void queryClient.invalidateQueries({ queryKey: ['triagem-portabilidade-concluida'] });
      void queryClient.invalidateQueries({ queryKey: ['portabilidade-extras'] });
      marcarTelasDeForaDesatualizadas(queryClient);
    },
    onError: (error) => toast({ title: 'Não reagendou', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' }),
  });

  const { data: itensTodos = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-portabilidade-concluida', profile?.id, profile?.funcao],
    queryFn: async () => {
      const equipes = await equipesDoBko(profile);
      let q = supabase
        .from('v_planilha_mae')
        .select(STANDARD_SELECT_FIELDS + ', data_agendamento')
        .eq('coluna_atual', 'PORTABILIDADE AGENDADA')
        .eq('quadro_atual', 'PRE_ATIVO');
      if (equipes) q = q.in('equipe_id', equipes);
      q = filtrarPeloLider(q, profile, await equipesDoLider(profile));
      const { data, error } = await q
        .order('data_agendamento', { ascending: true, nullsFirst: true })
        .order('cnpj', { ascending: true })
        .order('pedido_id', { ascending: true });

      if (error) { onQueryError(error); throw error; }
      onQuerySuccess();

      // ⛔ Pré-Ativo inteiro DAS SUAS EQUIPES (Douglas 29/09/2026): sem recorte
      // por dono (owner_qualidade_id), mas nunca equipe alheia.
      const filtered = data || [];

      let lastCnpj = '';
      const processed: ItemPortabilidadeConcluida[] = (filtered as LinhaPlanilha[]).map((item: LinhaPlanilha) => {
        const isFirst = item.cnpj !== lastCnpj;
        lastCnpj = item.cnpj || '';
        return {
          item_id: item.item_id,
          pedido_id: item.pedido_id,
          numero_pedido: String(item.numero_pedido || ''),
          item_seq: item.item_seq ?? null,
          cnpj: item.cnpj || '',
          tipo_pessoa: item.tipo_pessoa ?? null,
          documento: item.documento || item.cnpj || '',
          razao_social: item.razao_social || '',
          numero_cotacao_operadora: item.numero_cotacao_operadora || '',
          linhas_formatadas: buildLinhasFormatadas(item),
          operadora_doadora: getOperadoraWithFallback(item),
          plano_nome: item.plano_nome || '',
          tipo_chip: item.tipo_chip || '',
          faturamento_tipo: item.faturamento_tipo || '',
          agenda_tipo: item.agenda_tipo || '',
          data_agendamento: item.data_agendamento || '',
          cessionario_nome: item.cessionario_nome || '',
          cessionario_cpf: item.cessionario_cpf || '',
          entrada_coluna_em: item.entrada_coluna_em || '',
          is_first_of_cnpj: isFirst,
          cliente_id: item.cliente_id || '',
          telefone: item.telefone_principal || '',
          email: item.contato_real_email || '',
          status_id: item.status_id || '',
          coluna_atual: item.coluna_atual || 'PORTABILIDADE AGENDADA',
          quadro_atual: item.quadro_atual || 'PRE_ATIVO',
        };
      });
      return processed;
    },
    refetchInterval,
  });

  const handlePnConcluido = (item: ItemPortabilidadeConcluida) => {
    rodar(`${item.item_id}|concluir`, () => finalizarPedidoAsync({
      item_id: item.item_id,
      pedido_id: item.pedido_id,
      cnpj: item.cnpj,
      razao_social: item.razao_social,
      telefone: item.telefone,
      email: item.email,
      cliente_id: item.cliente_id,
      status_anterior_id: item.status_id,
      coluna_anterior: item.coluna_atual,
      quadro_anterior: item.quadro_atual,
      acao: 'pn_concluido',
    }));
  };

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). Lei em
     `triagem/segmentoTriagem.ts`: filtro, nunca coluna nova. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const itens = useMemo(
    () => itensTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)),
    [itensTodos, segmento],
  );
  const { data: extras } = usePortabilidadeExtras(itens.map(i => i.item_id), isOpen);
  const uniquePedidos = new Set(itens.map((i) => i.pedido_id)).size;
  const vencidas = itens.filter(i => situacaoAgenda(i.data_agendamento).vencida).length;

  return (
    <>
      <TriagemShell
        isOpen={isOpen}
        onFechar={onClose}
        Icone={CheckCircle2}
        titulo="Triagem Portabilidade Agendada"
        contadores={{ pedidos: uniquePedidos, itens: itens.length, valor: 0 }}
        cabecalhoExtra={<span className={cn('whitespace-nowrap rounded-lg border border-border bg-card px-3 py-1 text-[11px]', vencidas ? 'text-primary' : 'text-foreground')}>{vencidas} com a hora vencida</span>}
        segmento={segmento}
        onSegmento={setSegmento}
        sla={null}
        fifo={false}
        legenda={[
          { key: 'manter', Icone: Clock, label: 'Justificar e manter' },
          { key: 'pn-conclu-do', Icone: Check, label: '(PN) Concluído' },
          { key: 'reagendar', Icone: CalendarClock, label: 'Reagendar' },
          { key: 'historico', Icone: History, label: 'Histórico' },
          { key: 'pn-negada', Icone: Ban, label: '(PN) Negada' },
        ]}
        isLoading={isLoading}
        onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
        vazio={
          itens.length === 0
            ? { titulo: 'Nenhum item pendente', descricao: 'Todos os itens foram processados' }
            : undefined
        }
      >
        <table className="w-full" style={{ tableLayout: 'fixed', minWidth: TRIAGEM_LARGURA_MINIMA_PADRAO }}>
          <TriagemStandardColGroup extraCols={<>
            <col style={{ width: '9%' }} /><col style={{ width: '6%' }} /><col style={{ width: '6%' }} /><col style={{ width: '12%' }} /><col style={{ width: '228px' }} />
          </>} />
          <thead className="sticky top-0 z-10 bg-surface">
            <tr>
              <TriagemStandardHeaders rotuloDocumento={rotuloSegmento(segmento)} />
              <th className={TRIAGEM_TH}>Portabilidade</th>
              <th className={TRIAGEM_TH} title="O sistema avisa o cliente na véspera (das 18h às 21h) ou no próprio dia (até 1 h antes), com as linhas e o horário — um aviso por cotação. O registro é a timeline do pedido.">Aviso do sistema</th>
              <th className={TRIAGEM_TH} title="Tempo desde a primeira entrada no Pré-Ativo">Pré-ativo</th>
              <th className={TRIAGEM_TH} title="Consulta da operadora 2h depois da hora marcada. Evidência, não prova.">Operadora</th>
              <th className={TRIAGEM_TH}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => {
              const ex = extras?.get(item.item_id);
              const ag = situacaoAgenda(item.data_agendamento);
              const alvo = { itemId: item.item_id, razaoSocial: item.razao_social, numeroPedido: item.numero_pedido };
              const concluindo = ocupada(`${item.item_id}|concluir`);
              const ocupadaLinha = concluindo || ocupada(chaveDaCotacao(item));
              return (
                <tr key={item.item_id} className={cn(
                  'box-border h-[33px] border-b border-border/30 transition-colors hover:bg-muted/30',
                  !item.is_first_of_cnpj && 'bg-muted/10',
                  ag.vencida && 'shadow-[inset_3px_0_0_hsl(var(--primary))]',
                )}>
                  <TriagemStandardCells item={item} />
                  <td className={TRIAGEM_TD}>
                    <span className="flex min-w-0 items-baseline gap-1.5" title={`Agendada para ${ag.texto}`}>
                      <span className="font-display text-[12px] font-bold tabular-nums text-foreground">{ag.texto}</span>
                      <span className={cn('truncate text-[10px]', ag.vencida ? 'font-semibold text-primary' : 'text-muted-label')}>{ag.rotulo}</span>
                    </span>
                  </td>
                  <td className={TRIAGEM_TD}>
                    {/* Lido da TIMELINE: é o registro oficial do aviso (25/09/2026). */}
                    {ex?.aviso_vespera_em
                      ? <span className={cn(TRIAGEM_SELO_BASE, TRIAGEM_SELO_OK, 'tabular-nums')} title={`Avisado pelo sistema em ${new Date(ex.aviso_vespera_em).toLocaleString('pt-BR')} — registrado na timeline`}>
                          avisado {new Date(ex.aviso_vespera_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      : ex?.aviso_vespera_status === 'falhou' || ex?.aviso_vespera_status === 'incerto'
                        ? <span className={cn(TRIAGEM_SELO_BASE, TRIAGEM_SELO_PENDENTE)} title="O aviso não saiu — o motivo está na timeline do pedido">{ex.aviso_vespera_status === 'incerto' ? 'envio incerto' : 'não saiu'}</span>
                        : ex?.aviso_vespera_status === 'enviando'
                          ? <span className="text-[10px] text-muted-label">enviando</span>
                          : <span className="text-[10px] text-muted-label">{ag.futura ? 'véspera ou no dia' : 'não saiu'}</span>}
                  </td>
                  <td className={TRIAGEM_TD}><SeloPreAtivo extras={ex} /></td>
                  <td className={TRIAGEM_TD}>
                    {ag.futura ? <span className="text-[10px] text-muted-label">depois da hora</span> : <SeloOperadora extras={ex} />}
                  </td>
                  <td className={TRIAGEM_TD}>
                    <AcoesTriagem
                      manter={
                        <BotaoManter
                          title="Manter — consultei e ainda não mudou nada. Justificar e mover para o fim da fila."
                          onClick={() => setManterItem(item)} disabled={ocupadaLinha}
                          carregando={ocupada(chaveDaCotacao(item))} />
                      }
                      avancar={
                        <Button size="icon" variant="ghost" className={TRIAGEM_BTN_PRIMARIO}
                          title={ag.futura
                            ? `Agendada para ${ag.texto}: só conclui a partir do dia da portabilidade. Se portou antes, reagende com a data real.`
                            : '(PN) Concluído — finalizar com boas-vindas'}
                          onClick={() => handlePnConcluido(item)} disabled={ocupadaLinha || ag.futura}>
                          {concluindo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        </Button>
                      }
                      desvios={
                        <Popover open={reagendarItem === item.item_id} onOpenChange={(o) => {
                          setReagendarItem(o ? item.item_id : null);
                          setNovaData('');
                          setMotivoReagendar('');
                        }}>
                          <PopoverTrigger asChild>
                            <Button size="icon" variant="ghost" className={TRIAGEM_BTN}
                              title="Reagendar — nova data e hora" disabled={ocupadaLinha}>
                              <CalendarClock className="h-3.5 w-3.5" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 p-3" align="end">
                            <p className="mb-1 text-xs font-semibold">Reagendar portabilidade</p>
                            <p className="mb-2 text-[10px] text-muted-foreground">Hoje: {ag.texto}. A data muda em todas as linhas da cotação; precisa ser depois de agora.</p>
                            <div className="grid gap-2">
                              <Input type="datetime-local" value={novaData} onChange={(e) => setNovaData(e.target.value)} className="h-8 text-xs" />
                              <Input value={motivoReagendar} onChange={(e) => setMotivoReagendar(e.target.value)} placeholder="Motivo do reagendamento" className="h-8 text-xs" />
                              <Button size="sm" className="h-8 px-3"
                                disabled={!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(novaData) || motivoReagendar.trim().length < 10 || reagendar.isPending}
                                onClick={() => rodar(chaveDaCotacao(item), () => reagendar.mutateAsync({ item, data: novaData, motivo: motivoReagendar }))}>
                                {reagendar.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Reagendar'}
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      }
                      ferramentas={
                        <Button size="icon" variant="ghost" className={TRIAGEM_BTN}
                          title="Histórico da portabilidade" onClick={() => setHistorico(alvo)}>
                          <History className="h-3.5 w-3.5" />
                        </Button>
                      }
                      cancelar={
                        <Button size="icon" variant="ghost" className={TRIAGEM_BTN}
                          title="(PN) Negada - Cancelar pedido"
                          onClick={() => { setCancelarItem(item); setCancelarOpen(true); }} disabled={ocupadaLinha}>
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TriagemShell>

      <NumeroLinhaHaDialog {...numeroHaDialog} />

      {cancelarItem && (
        <CancelarItemModal
          open={cancelarOpen}
          onOpenChange={setCancelarOpen}
          itemId={cancelarItem.item_id}
          pedidoId={cancelarItem.pedido_id}
          itemNome={`${cancelarItem.razao_social} - ${cancelarItem.numero_pedido}`}
          onSuccess={() => {
            setCancelarItem(null);
            refetch();
          }}
        />
      )}

      {manterItem && (
        <ManterComNotaModal
          open={!!manterItem}
          onOpenChange={(open) => !open && setManterItem(null)}
          pedidoId={manterItem.pedido_id}
          clienteId={manterItem.cliente_id || ''}
          onConfirm={() => {
            const alvoManter = manterItem;
            rodar(chaveDaCotacao(alvoManter), () => moverParaFinal.mutateAsync(alvoManter.item_id));
            setManterItem(null);
          }}
          justificarFila={{
            itemId: manterItem.item_id,
            coluna: manterItem.coluna_atual,
            numeroPedido: manterItem.numero_pedido,
            razaoSocial: manterItem.razao_social,
            justificativaAtual: extras?.get(manterItem.item_id)?.ultima_tratativa?.texto ?? null,
            diasParado: diasDesde(extras?.get(manterItem.item_id)?.entrou_coluna_em ?? manterItem.entrada_coluna_em),
          }}
        />
      )}
      <HistoricoPortabilidadeDialog alvo={historico} onClose={() => setHistorico(null)} />
    </>
  );
}
